import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  getCurrentUser,
  login as loginRequest,
  logout as logoutRequest,
  requestEmailMfaCode as requestEmailMfaCodeRequest,
  signup as signupRequest,
  verifyEmailMfaLogin as verifyEmailMfaLoginRequest,
  verifyTotpLogin as verifyTotpLoginRequest,
  type GreenUnisonUser,
  type LoginInput,
  type MfaVerifyInput,
  type SignupInput,
} from "../lib/greenunisonApi";
import { getAuthToken, setAuthToken } from "../lib/http";

const MFA_CHALLENGE_KEY = "gu_mfa_challenge";

export interface LoginResult {
  user: GreenUnisonUser | null;
  token: string | null;
  requiresTOTPVerification?: boolean;
  requiresEmailVerification?: boolean;
}

export interface MfaChallenge {
  userId: string;
  email: string;
  method: "totp" | "email";
  nextPath: string;
  rememberMe?: boolean;
}

interface AuthContextValue {
  user: GreenUnisonUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaChallenge: MfaChallenge | null;
  canCreatePosts: boolean;
  login(input: LoginInput): Promise<LoginResult>;
  signup(input: SignupInput): Promise<GreenUnisonUser | null>;
  logout(): Promise<void>;
  refreshUser(): Promise<GreenUnisonUser | null>;
  setMfaChallenge(challenge: MfaChallenge | null): void;
  requestEmailMfaCode(userId: string): Promise<void>;
  verifyTotpLogin(input: MfaVerifyInput): Promise<GreenUnisonUser | null>;
  verifyEmailMfaLogin(input: MfaVerifyInput): Promise<GreenUnisonUser | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function getStoredMfaChallenge() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(MFA_CHALLENGE_KEY);
    return raw ? (JSON.parse(raw) as MfaChallenge) : null;
  } catch {
    return null;
  }
}

function storeMfaChallenge(challenge: MfaChallenge | null) {
  if (typeof window === "undefined") return;

  if (challenge) {
    window.sessionStorage.setItem(MFA_CHALLENGE_KEY, JSON.stringify(challenge));
  } else {
    window.sessionStorage.removeItem(MFA_CHALLENGE_KEY);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<GreenUnisonUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mfaChallenge, setMfaChallengeState] = useState<MfaChallenge | null>(null);

  const setMfaChallenge = useCallback((challenge: MfaChallenge | null) => {
    setMfaChallengeState(challenge);
    storeMfaChallenge(challenge);
  }, []);

  const refreshUser = useCallback(async () => {
    const currentToken = getAuthToken();
    setToken(currentToken);

    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      return currentUser;
    } catch {
      setAuthToken(null);
      setToken(null);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    setMfaChallengeState(getStoredMfaChallenge());
    setIsLoading(true);
    refreshUser().finally(() => setIsLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (input: LoginInput) => {
    const result = await loginRequest(input);

    if (result.requiresTOTPVerification || result.requiresEmailVerification) {
      setToken(null);
      setUser(null);
      return result;
    }

    setToken(result.token);
    setUser(result.user);
    setMfaChallenge(null);
    return result;
  }, [setMfaChallenge]);

  const signup = useCallback(async (input: SignupInput) => {
    const result = await signupRequest(input);
    setToken(result.token);
    setUser(result.user);
    setMfaChallenge(null);
    return result.user;
  }, [setMfaChallenge]);

  const requestEmailMfaCode = useCallback(async (userId: string) => {
    await requestEmailMfaCodeRequest(userId);
  }, []);

  const verifyTotpLogin = useCallback(async (input: MfaVerifyInput) => {
    const result = await verifyTotpLoginRequest(input);
    setToken(result.token);
    setUser(result.user);
    setMfaChallenge(null);
    return result.user;
  }, [setMfaChallenge]);

  const verifyEmailMfaLogin = useCallback(async (input: MfaVerifyInput) => {
    const result = await verifyEmailMfaLoginRequest(input);
    setToken(result.token);
    setUser(result.user);
    setMfaChallenge(null);
    return result.user;
  }, [setMfaChallenge]);

  const logout = useCallback(async () => {
    await logoutRequest();
    setToken(null);
    setUser(null);
    setMfaChallenge(null);
  }, [setMfaChallenge]);

  const canCreatePosts = useMemo(() => {
    const roles = user?.roles ?? [];
    const permissions = user?.permissions ?? [];
    return (
      permissions.includes("blog.posts.create") ||
      roles.some((role) => ["admin", "blog_editor", "blog_author"].includes(role))
    );
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(user),
      isLoading,
      mfaChallenge,
      canCreatePosts,
      login,
      signup,
      logout,
      refreshUser,
      setMfaChallenge,
      requestEmailMfaCode,
      verifyTotpLogin,
      verifyEmailMfaLogin,
    }),
    [
      canCreatePosts,
      isLoading,
      login,
      logout,
      mfaChallenge,
      refreshUser,
      requestEmailMfaCode,
      setMfaChallenge,
      signup,
      token,
      user,
      verifyEmailMfaLogin,
      verifyTotpLogin,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return value;
}
